// Pestaña «Productos» de la ficha: el mix por categoría, que filtra, y la tabla de compras contigo.
import { useMemo, useState } from 'react';
import { LayoutGrid } from 'lucide-react';
import type {
    PortalCustomerDetail,
    PortalCustomerPurchase,
} from '@/api/services/retail-wizard/contract';
import { cn } from '@/lib/cn';
import { formatDateMx, formatShortDateMx } from '@/lib/dateHelpers';
import { formatMXN } from '@/shared/lib/format/money';
import { DataTable, type DataTableColumn } from '@/shared/patterns/DataTable';
import {
    CategoryIcon,
    SaleTicketProduct,
    ticketHeadline,
    ticketTitle,
    useTicketExpansion,
    type TicketCategory,
} from '@/shared/patterns/SaleTicket';
import { PENDING_CELL } from '../lib/customersFormat';
import {
    CATEGORY_LABEL,
    categoryCards,
    DEFAULT_PURCHASES_SORT,
    distinctSkuCount,
    filterPurchases,
    nextDetailSort,
    PAYMENT_CHIP_CLASS,
    paymentChipOf,
    productLabel,
    purchaseCategoryMix,
    salesCountLabel,
    sortPurchases,
    ticketCategoryOf,
    type CategoryKey,
    type PurchasesSort,
    type PurchasesSortKey,
} from '../lib/portalCustomerFormat';
import { DetailCard } from './DetailCard';

const TEXT_SORT_KEYS: PurchasesSortKey[] = ['product', 'payment'];

function CategoryCardIcon({ category }: { category: CategoryKey }) {
    return category === 'all' ? (
        <LayoutGrid className="size-full" aria-hidden />
    ) : (
        <CategoryIcon category={category} className="size-full" />
    );
}

/** Título de la celda: las líneas del ticket con su SKU y la tienda. */
function productTitle(purchase: PortalCustomerPurchase): string {
    return [ticketTitle(purchase, productLabel(purchase)), purchase.storeName]
        .filter(Boolean)
        .join(' · ');
}

/** El ticket y, abajo, el SKU que encabeza (si no es ya su nombre) y la tienda. */
function ProductCell({
    purchase,
    category,
}: {
    purchase: PortalCustomerPurchase;
    category: TicketCategory;
}) {
    const saleName = productLabel(purchase);
    const { sku, name } = ticketHeadline(purchase, category, saleName);
    return (
        <>
            <div className="font-medium">
                <SaleTicketProduct sale={purchase} category={category} saleName={saleName} />
            </div>
            <div className="mt-px truncate text-[9.5px] text-muted-foreground">
                {sku && sku !== name ? <span className="font-mono">{sku} · </span> : null}
                {purchase.storeName ?? PENDING_CELL}
            </div>
        </>
    );
}

function purchaseColumns(
    category: TicketCategory,
): DataTableColumn<PortalCustomerPurchase, PurchasesSortKey>[] {
    return [
        {
            key: 'soldAt',
            label: 'Fecha',
            width: '66px',
            sortKey: 'soldAt',
            cellClassName: () => 'text-[10.5px] tabular-nums text-muted-foreground',
            cellTitle: (purchase) => formatDateMx(purchase.soldAt),
            cell: (purchase) => formatShortDateMx(purchase.soldAt),
        },
        {
            key: 'product',
            label: 'Producto',
            sortKey: 'product',
            cellTitle: productTitle,
            // La tienda baja a la segunda línea: con su propia columna el producto no cabía en la ficha.
            cell: (purchase) => <ProductCell purchase={purchase} category={category} />,
        },
        {
            key: 'payment',
            label: 'Pago',
            width: '76px',
            sortKey: 'payment',
            cellOverflow: 'nowrap',
            cell: (purchase) => {
                const chip = paymentChipOf(purchase);
                return (
                    <span
                        className={cn(
                            'inline-flex items-center rounded-[3px] px-1.5 py-px text-[9.5px] font-semibold',
                            PAYMENT_CHIP_CLASS[chip.tone],
                        )}
                    >
                        {chip.label}
                    </span>
                );
            },
        },
        {
            key: 'soldCents',
            label: 'Monto',
            width: '80px',
            numeric: true,
            sortKey: 'soldCents',
            cellClassName: () => 'font-bold',
            cell: (purchase) => formatMXN(purchase.soldCents / 100),
        },
    ];
}

export function CustomerPurchasesTab({ detail }: { detail: PortalCustomerDetail }) {
    const [category, setCategory] = useState<CategoryKey>('all');
    const [sort, setSort] = useState<PurchasesSort>(DEFAULT_PURCHASES_SORT);
    const ticketCategory = ticketCategoryOf(category);
    const { expandColumn, tableProps } = useTicketExpansion<
        PortalCustomerPurchase,
        PurchasesSortKey
    >({ category: ticketCategory, totalCentsOf: (purchase) => purchase.soldCents });
    const columns = [expandColumn, ...purchaseColumns(ticketCategory)];

    const cards = categoryCards(purchaseCategoryMix(detail.purchases), detail.summary);
    const rows = useMemo(
        () => sortPurchases(filterPurchases(detail.purchases, category), sort, category),
        [detail.purchases, category, sort],
    );
    const skuCount = distinctSkuCount(detail.purchases);
    const title = category === 'all' ? 'Compras' : `Compras · ${CATEGORY_LABEL[category]}`;

    return (
        <>
            <DetailCard title="Mix por categoría">
                <div className="grid grid-cols-[repeat(auto-fill,minmax(132px,1fr))] gap-2">
                    {cards.map((card) => {
                        const active = card.key === category;
                        return (
                            <button
                                key={card.key}
                                type="button"
                                aria-pressed={active}
                                onClick={() => {
                                    setCategory(card.key);
                                }}
                                className={cn(
                                    'flex min-w-0 items-center gap-2 rounded-[5px] border px-2.5 py-2 text-left transition-colors',
                                    active
                                        ? 'border-border bg-accent'
                                        : 'border-border/50 bg-muted/40 hover:border-border hover:bg-muted/70',
                                    card.purchasesCount === 0 && !active && 'opacity-50',
                                )}
                            >
                                <span
                                    className={cn(
                                        'size-[18px] flex-none',
                                        active ? 'text-primary' : 'text-muted-foreground',
                                    )}
                                >
                                    <CategoryCardIcon category={card.key} />
                                </span>
                                <span className="min-w-0">
                                    <span
                                        className={cn(
                                            'block truncate text-[9px] font-bold uppercase tracking-[0.04em]',
                                            active ? 'text-foreground' : 'text-muted-foreground',
                                        )}
                                    >
                                        {card.label}
                                    </span>
                                    <span className="mt-0.5 block text-[14px] font-bold tabular-nums text-foreground">
                                        {card.purchasesCount}{' '}
                                        <span className="text-[9.5px] font-medium text-muted-foreground">
                                            {salesCountLabel(card.purchasesCount)}
                                        </span>
                                    </span>
                                    <span className="mt-px block truncate text-[10.5px] tabular-nums text-muted-foreground">
                                        {formatMXN(card.totalSoldCents / 100)}
                                    </span>
                                </span>
                            </button>
                        );
                    })}
                </div>
            </DetailCard>

            <DetailCard
                title={`${title} · ${String(rows.length)} ${rows.length === 1 ? 'ticket' : 'tickets'}`}
                aside={
                    category === 'all' ? (
                        `${String(skuCount)} ${skuCount === 1 ? 'SKU distinto' : 'SKUs distintos'}`
                    ) : (
                        <button
                            type="button"
                            onClick={() => {
                                setCategory('all');
                            }}
                            className="text-[10.5px] text-muted-foreground hover:text-foreground"
                        >
                            × quitar filtro
                        </button>
                    )
                }
                flush
            >
                <DataTable
                    columns={columns}
                    rows={rows}
                    rowKey={(purchase) => purchase.saleId}
                    caption="Compras contigo"
                    sort={sort}
                    onSortToggle={(key) => {
                        setSort(nextDetailSort(sort, key, TEXT_SORT_KEYS));
                    }}
                    {...tableProps}
                    empty={<span className="text-[10.5px] italic">Sin compras registradas.</span>}
                    className="max-h-[588px] flex-none"
                />
            </DetailCard>
        </>
    );
}
